export interface XmlLine {
  text: string
  isTarget: boolean
  nodeId?: string
  collapsed?: boolean
  isSummary?: boolean
  isBase64Summary?: boolean
}

export function isBase64Like(text: string): boolean {
  if (text.length < 200) return false
  const normalized = text.replace(/[\s\r\n]/g, '')
  return normalized.length > 100 && /^[A-Za-z0-9+/=]+$/.test(normalized)
}

export function findBase64ElementPaths(doc: Document): string[] {
  const paths: string[] = []
  function walk(el: Element, path: string): void {
    const children = Array.from(el.children)
    if (children.length === 0) {
      const text = Array.from(el.childNodes)
        .filter(n => n.nodeType === Node.TEXT_NODE)
        .map(n => (n.textContent ?? '').trim())
        .join('')
      if (isBase64Like(text)) paths.push(path)
    } else {
      children.forEach((child, i) => walk(child, `${path}/${i}`))
    }
  }
  if (doc.documentElement) walk(doc.documentElement, '0')
  return paths
}

export function resolveXPath(doc: Document, xpath: string): Element | null {
  const parts = xpath.split('/').filter(Boolean)
  let current: Element | null = null
  for (let i = 0; i < parts.length; i++) {
    const match = parts[i].match(/^([^[]+)(?:\[(\d+)\])?$/)
    if (!match) return null
    const name = match[1]
    const idx  = match[2] ? parseInt(match[2]) - 1 : 0
    if (i === 0) {
      const root = doc.documentElement
      if (root.localName !== name) return null
      current = root
    } else {
      if (!current) return null
      const matching: Element[] = Array.from(current.children).filter(c => c.localName === name)
      if (idx >= matching.length) return null
      current = matching[idx]
    }
  }
  return current
}

export function serializeXml(
  doc: Document,
  targetNode: Element | null,
  collapsed: Set<string>,
): XmlLine[] {
  const lines: XmlLine[] = []

  function walk(el: Element, depth: number, ancestorIsTarget: boolean, path: string): void {
    const indent  = '  '.repeat(depth)
    const tag     = el.localName
    const attrStr = Array.from(el.attributes)
      .filter(a => !a.name.startsWith('xmlns'))
      .map(a => ` ${a.name}="${a.value}"`)
      .join('')

    const isTarget  = el === targetNode
    const highlight = ancestorIsTarget || isTarget
    const children  = Array.from(el.children)
    const directText = Array.from(el.childNodes)
      .filter(n => n.nodeType === Node.TEXT_NODE)
      .map(n => (n.textContent ?? '').trim())
      .filter(Boolean)
      .join('')

    if (children.length === 0) {
      if (directText && isBase64Like(directText)) {
        const isCollapsed = collapsed.has(path)
        const sizeKb = Math.round(directText.replace(/\s/g, '').length * 0.75 / 1024)
        lines.push({ text: `${indent}<${tag}${attrStr}>`, isTarget: highlight, nodeId: path, collapsed: isCollapsed })
        if (isCollapsed) {
          lines.push({
            text: `${indent}  ⋯ base64-bijlage, ~${sizeKb} KB`,
            isTarget: false,
            isSummary: true,
            isBase64Summary: true,
            nodeId: path,
          })
        } else {
          lines.push({ text: `${indent}  ${directText.replace(/[\r\n\s]+/g, '')}`, isTarget: highlight })
        }
        lines.push({ text: `${indent}</${tag}>`, isTarget: false })
      } else if (directText) {
        lines.push({ text: `${indent}<${tag}${attrStr}>${directText}</${tag}>`, isTarget: highlight })
      } else {
        lines.push({ text: `${indent}<${tag}${attrStr} />`, isTarget: highlight })
      }
    } else {
      const isCollapsed = collapsed.has(path)
      lines.push({ text: `${indent}<${tag}${attrStr}>`, isTarget: highlight, nodeId: path, collapsed: isCollapsed })

      if (isCollapsed) {
        const count = children.length
        lines.push({
          text: `${indent}  ⋯ ${count} ${count === 1 ? 'element' : 'elementen'} verborgen`,
          isTarget: false,
          isSummary: true,
          nodeId: path,
        })
        lines.push({ text: `${indent}</${tag}>`, isTarget: false })
      } else {
        for (let i = 0; i < children.length; i++) {
          walk(children[i], depth + 1, highlight, `${path}/${i}`)
        }
        lines.push({ text: `${indent}</${tag}>`, isTarget: false })
      }
    }
  }

  if (doc.documentElement) {
    walk(doc.documentElement, 0, false, '0')
  }
  return lines
}

export function getElementPath(doc: Document, el: Element): string {
  const parts: number[] = []
  let current: Element | null = el
  while (current && current !== doc.documentElement) {
    const parent: Element | null = current.parentElement
    if (!parent) break
    parts.unshift(Array.from(parent.children).indexOf(current))
    current = parent
  }
  return '0' + (parts.length > 0 ? '/' + parts.join('/') : '')
}

export function getAncestorPaths(path: string): string[] {
  const parts = path.split('/')
  return Array.from({ length: parts.length - 1 }, (_, i) =>
    parts.slice(0, i + 1).join('/')
  )
}
